// ────────────────────────────────────────────────────────────────────────────
//  referralLink.ts — Auto-Apply von ?ref=CODE Werbe-Links
//
//  Flow:
//    1. Beim Landing/jeder Seite: `captureFromURL()` schaut nach ?ref=CODE
//       in der URL und speichert ihn in localStorage (überlebt Navigation
//       Login → Register → Dashboard).
//    2. Sobald der User authenticated ist: `applyPendingIfNeeded()` schickt
//       den gespeicherten Code an /referrals/apply. Bei Erfolg/Konflikt
//       wird localStorage gelöscht (verhindert Endlos-Retry).
//    3. Failure-Klassen:
//        409 (schon Referrer) → silent löschen (User wurde schon geworben)
//        409 (schon Käufe)    → silent löschen (zu spät)
//        404 (Code falsch)    → silent löschen + Warnung in Console
//        500/Network          → KEEPS code (versucht beim nächsten Mount nochmal)
// ────────────────────────────────────────────────────────────────────────────

import { api } from './api'

const KEY = 'custosoft.pendingReferralCode'

/**
 * Liest ?ref= aus window.location.search und persistiert in localStorage.
 * Idempotent — kann von mehreren Mount-Stellen aus aufgerufen werden.
 *
 * Entfernt den Param aus der URL via history.replaceState damit der User
 * nicht permanent eine ?ref=… in seinem Browser-Tab hat (kosmetisch + privacy).
 */
export function captureReferralFromURL(): void {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    const code = url.searchParams.get('ref') ?? url.searchParams.get('referral')
    if (!code) return
    const clean = code.trim().toUpperCase()
    if (!/^[A-Z0-9]{6,12}$/.test(clean)) return

    // Speichern (überschreibt nur falls noch keiner gespeichert ist —
    // verhindert dass ein offener Tab mit altem Link einen neuen Klick
    // überschreibt)
    if (!localStorage.getItem(KEY)) {
      localStorage.setItem(KEY, clean)
    }

    // Aus URL entfernen
    url.searchParams.delete('ref')
    url.searchParams.delete('referral')
    const newUrl = url.pathname + (url.search ? url.search : '') + url.hash
    window.history.replaceState({}, '', newUrl)
  } catch {
    // Kein Browser / kein localStorage → silent skip
  }
}

/**
 * Gibt den aktuell gespeicherten Code zurück (oder null).
 */
export function getPendingReferralCode(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return localStorage.getItem(KEY)
  } catch {
    return null
  }
}

/**
 * Löscht den gespeicherten Code.
 */
export function clearPendingReferralCode(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(KEY)
  } catch {}
}

/**
 * Wenn ein Code gespeichert ist UND der User authenticated ist (Bearer-Token
 * vorhanden — automatisch über api.post), ruft /referrals/apply auf.
 *
 * Bei Success/erwarteten Fehlern (409/404/400) → Code löschen.
 * Bei unerwarteten Fehlern (Network/500) → Code behalten, beim nächsten Mount neu versuchen.
 *
 * Liefert ein Objekt mit Status für die UI.
 */
export async function applyPendingReferralIfAuthenticated(): Promise<
  | { status: 'none' }                                  // kein Code gespeichert
  | { status: 'applied'; message: string }              // erfolgreich angewendet
  | { status: 'rejected'; error: string }               // 4xx (zu spät, Code falsch, …)
  | { status: 'retry-later' }                            // 5xx / Network
> {
  const code = getPendingReferralCode()
  if (!code) return { status: 'none' }

  try {
    const resp = await api.post<{ ok?: boolean; message?: string; error?: string }>(
      '/referrals/apply',
      { code },
    )
    if (resp.error) {
      clearPendingReferralCode()
      return { status: 'rejected', error: resp.error }
    }
    clearPendingReferralCode()
    return { status: 'applied', message: resp.message ?? 'Werbe-Code eingelöst!' }
  } catch (e: any) {
    const msg = e?.message ?? 'Unbekannter Fehler'
    const status = e?.status ?? 0

    // Erwartete Client-Fehler → Code löschen, sonst hängt's da für immer
    if (status >= 400 && status < 500) {
      clearPendingReferralCode()
      return { status: 'rejected', error: msg }
    }

    // 5xx oder Network — Code behalten, beim nächsten Mount nochmal probieren
    return { status: 'retry-later' }
  }
}
